import React, {useState, useCallback, useRef, useEffect} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import LinkIcon from "@mui/icons-material/Link";
import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import CheckIcon from "@mui/icons-material/Check";

/**
 * Accessible social and direct sharing bar for news articles.
 * Includes clipboard copy with aria-live feedback, mailto, Facebook, and LinkedIn intents.
 */
export const ArticleShareBar = ({title, url}) => {
    const [copied, setCopied] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
    const shareTitle = title || "Bekymringsmestring";

    const handleCopy = useCallback(async () => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                setSnackbarOpen(true);
                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => setCopied(false), 3000);
            }
        } catch (err) {
            console.error("Failed to copy URL:", err);
        }
    }, [shareUrl]);

    const mailtoHref = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(
        `Les denne artikkelen på Bekymringsmestring:\n\n${shareUrl}`
    )}`;

    const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    const linkedInHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
                py: 2,
                borderTop: "1px solid",
                borderBottom: "1px solid",
                borderColor: "rgba(40, 82, 56, 0.12)",
                my: 3,
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                    mr: 0.5,
                }}
            >
                Del artikkelen:
            </Typography>

            {/* Copy Link Button */}
            <Button
                variant="outlined"
                size="small"
                color={copied ? "success" : "primary"}
                startIcon={copied ? <CheckIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
                onClick={handleCopy}
                aria-label="Kopier lenke til artikkelen"
                sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    minHeight: 44,
                    px: 1.75,
                    "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                }}
            >
                {copied ? "Kopiert!" : "Kopier lenke"}
            </Button>

            {/* Email Share */}
            <Tooltip title="Del via e-post" arrow>
                <IconButton
                    component="a"
                    href={mailtoHref}
                    aria-label="Del via e-post"
                    sx={{
                        width: 44,
                        height: 44,
                        color: "primary.main",
                        bgcolor: "rgba(40, 82, 56, 0.06)",
                        "&:hover": {bgcolor: "rgba(40, 82, 56, 0.12)"},
                        "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                    }}
                >
                    <EmailIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* Facebook Share */}
            <Tooltip title="Del på Facebook" arrow>
                <IconButton
                    component="a"
                    href={facebookHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Del på Facebook"
                    sx={{
                        width: 44,
                        height: 44,
                        color: "#1877F2",
                        bgcolor: "rgba(24, 119, 242, 0.08)",
                        "&:hover": {bgcolor: "rgba(24, 119, 242, 0.16)"},
                        "&:focus-visible": {outline: "2px solid #1877F2", outlineOffset: 2},
                    }}
                >
                    <FacebookIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* LinkedIn Share */}
            <Tooltip title="Del på LinkedIn" arrow>
                <IconButton
                    component="a"
                    href={linkedInHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Del på LinkedIn"
                    sx={{
                        width: 44,
                        height: 44,
                        color: "#0A66C2",
                        bgcolor: "rgba(10, 102, 194, 0.08)",
                        "&:hover": {bgcolor: "rgba(10, 102, 194, 0.16)"},
                        "&:focus-visible": {outline: "2px solid #0A66C2", outlineOffset: 2},
                    }}
                >
                    <LinkedInIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* Feedback Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="success"
                    variant="filled"
                    sx={{fontWeight: 600, borderRadius: 2}}
                >
                    Lenken er kopiert til utklippstavlen!
                </Alert>
            </Snackbar>
        </Box>
    );
};
